<script setup lang="ts">
/**
 * What a figure of a linked table said in the version the report is read
 * against, and what it says now.
 *
 * Opened by the cell, and held open while the pointer is on it so the link to
 * CSRD can be pressed. It lives on the body, so neither the table's clipping
 * nor an export (which copies the page) can carry it.
 *
 * The history of the figure is CSRD's: this only says how to get there.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import type { CsrdLink } from '@/domain/section'
import { readingDate } from '@/features/csrdLink/format'
import type { CellDiff } from '@/features/versions/table'

const props = defineProps<{
  /** Where the cell is on the screen. */
  anchor: DOMRect
  cell: CellDiff
  number: number
  /** The table's link to CSRD; none for a table that is kept here. */
  link?: CsrdLink | undefined
  /** CSRD's collection screen for the table, or null when it is not known. */
  url?: string | null | undefined
  /** Who last changed the cell and when, where the journal could say. */
  last?: { author: string; at: string } | null | undefined
  /** Whether pressing the cell opens its history. */
  history?: boolean | undefined
}>()

defineEmits<{ enter: []; leave: [] }>()

const { t, locale } = useI18n()

const WIDTH = 288

/** Below the cell, or above it where the screen runs out; kept inside the window. */
const place = computed(() => {
  const left = Math.max(8, Math.min(props.anchor.right - WIDTH, window.innerWidth - WIDTH - 8))
  const below = props.anchor.bottom + 6
  const fits = below + 190 < window.innerHeight
  return fits
    ? { left: `${left}px`, top: `${below}px` }
    : { left: `${left}px`, bottom: `${window.innerHeight - props.anchor.top + 6}px` }
})

const figure = (value: number): string =>
  new Intl.NumberFormat(locale.value, { maximumFractionDigits: 2 }).format(value)

const sign = (value: number): string => (value > 0 ? '+' : value < 0 ? '−' : '')

/** `+12 000 (+2,67 %)`: how far it moved, and as a share of what it was. */
const gap = computed(() => {
  const { delta, before } = props.cell
  if (delta === null) return null
  const left = Number(
    before
      ?.replace(/[^\d,.\-−]/g, '')
      .replace(',', '.')
      .replace('−', '-'),
  )
  const share =
    Number.isFinite(left) && left !== 0
      ? ` (${sign(delta)}${figure(Math.abs((delta / left) * 100))} %)`
      : ''
  return { text: `${sign(delta)}${figure(Math.abs(delta))}${share}`, up: delta > 0 }
})

const title = computed(() => {
  const key = props.cell.status === 'added' ? 'titleNew' : gap.value ? 'title' : 'titleLabel'
  return t(`versions.cell.${key}`, { number: props.number })
})

const read = computed(() => (props.link ? readingDate(props.link.pulledAt, locale.value) : ''))
const where = computed(() => {
  const link = props.link
  return link ? [link.entityCode, link.ref || link.disclosure].filter(Boolean).join(' · ') : ''
})

const lastAt = computed(() => (props.last ? readingDate(props.last.at, locale.value) : ''))
</script>

<template>
  <Teleport to="body">
    <div
      class="tip"
      role="tooltip"
      :style="{ ...place, width: `${WIDTH}px` }"
      @mouseenter="$emit('enter')"
      @mouseleave="$emit('leave')"
    >
      <header class="tip__head">
        <strong>{{ title }}</strong>
        <span v-if="link" class="tip__source">{{ t('versions.cell.source') }}</span>
      </header>
      <dl class="tip__rows">
        <template v-if="cell.status === 'changed'">
          <dt>{{ t('versions.cell.was', { number }) }}</dt>
          <dd>{{ cell.before || t('versions.detail.wasEmpty') }}</dd>
        </template>
        <dt>{{ t('versions.cell.now') }}</dt>
        <dd>{{ cell.value }}</dd>
        <template v-if="gap">
          <dt>{{ t('versions.cell.gap') }}</dt>
          <dd :class="gap.up ? 'tip__up' : 'tip__down'">{{ gap.text }}</dd>
        </template>
        <template v-if="last">
          <dt>{{ t('versions.cell.last') }}</dt>
          <dd>
            {{ last.author }}<template v-if="lastAt">, {{ lastAt }}</template>
          </dd>
        </template>
        <template v-if="read">
          <dt>{{ t('versions.cell.read') }}</dt>
          <dd>{{ read }}</dd>
        </template>
        <template v-if="where">
          <dt>{{ t('versions.cell.where') }}</dt>
          <dd>{{ where }}</dd>
        </template>
      </dl>
      <p v-if="history" class="tip__hint">{{ t('versions.cell.open') }}</p>
      <a v-if="url" class="tip__link" :href="url" target="_blank" rel="noopener noreferrer">
        {{ t('versions.cell.history') }} ↗
      </a>
    </div>
  </Teleport>
</template>

<style scoped>
.tip {
  position: fixed;
  z-index: 2000;
  padding: 10px 12px;
  border-radius: 8px;
  background: #111c2b;
  color: #e6edf6;
  box-shadow: 0 8px 28px rgb(0 0 0 / 35%);
  font-size: 12px;
  line-height: 1.4;
}

.tip__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.tip__source {
  padding: 1px 7px;
  border-radius: 4px;
  background: rgb(120 140 255 / 22%);
  color: #aab8ff;
  font-size: 10.5px;
  font-weight: 600;
}

.tip__rows {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 3px 14px;
  margin: 0;
}

.tip__rows dt {
  color: #8a9bb0;
}

.tip__rows dd {
  margin: 0;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.tip__up {
  color: #5fd08f;
}

.tip__down {
  color: #ff8a80;
}

.tip__hint {
  margin: 9px 0 0;
  padding-top: 8px;
  border-top: 1px solid rgb(255 255 255 / 12%);
  color: #8a9bb0;
}

.tip__link {
  display: block;
  margin-top: 9px;
  padding-top: 8px;
  border-top: 1px solid rgb(255 255 255 / 12%);
  color: #aab8ff;
  text-decoration: none;
}

.tip__link:hover {
  text-decoration: underline;
}
</style>
